import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { BraintreeInstances, VenmoPaymentResponse } from '@/types/venmo';
import { VENMO_ERRORS } from '@/constants/venmo';
import { CheckoutContext } from '@/context/CheckoutContext';
import { braintreeService } from '@/services/braintreeService';
import { useTranslate } from '@/hooks/useTranslate';
import { getVenmoTokenizeErrorMessage } from '@/utils/venmo';

interface UseVenmoBraintreeParams {
  braintreePK?: string;
  onPaymentError?: (error: string) => void;
}

export const useVenmoBraintree = ({ braintreePK, onPaymentError }: UseVenmoBraintreeParams) => {
  const [instances, setInstances] = useState<BraintreeInstances>({
    venmo: null,
    dataCollector: null,
  });
  const [isInitializing, setIsInitializing] = useState(true);
  const isPaymentInProgress = useRef(false);
  const initializedPKRef = useRef<string | null>(null);

  const {
    payment: { isInPaymentProcess },
    actions: { runPayment, setError, setIsInPaymentProcess },
  } = useContext(CheckoutContext);
  const { t } = useTranslate();

  // setError is intentionally omitted from deps: the wrapper in
  // CheckoutContent is rebuilt every parent render, which would re-create
  // initializeBraintree and re-fire the effect below. The underlying React
  // setState setter is stable, so the captured reference stays correct.
  const initializeBraintree = useCallback(
    async (publicKey: string) => {
      setIsInitializing(true);
      try {
        const [venmo, dataCollector] = await Promise.all([
          braintreeService.createVenmoClient(publicKey),
          braintreeService.createBraintreeDataCollector(publicKey),
        ]);
        setInstances({ venmo, dataCollector });
      } catch {
        setError(VENMO_ERRORS.INITIALIZATION_FAILED);
        setInstances({ venmo: null, dataCollector: null });
      } finally {
        setIsInitializing(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (!braintreePK || initializedPKRef.current === braintreePK) {
      return;
    }
    initializedPKRef.current = braintreePK;
    initializeBraintree(braintreePK);
  }, [braintreePK, initializeBraintree]);

  const processPayment = useCallback(
    async (nonce: string) => {
      await runPayment<VenmoPaymentResponse>({
        nonce,
        deviceData: instances.dataCollector?.deviceData,
      });
    },
    [instances.dataCollector, runPayment]
  );

  const handleSubmit = useCallback(async () => {
    if (isPaymentInProgress.current || isInPaymentProcess) {
      return;
    }

    if (!instances.venmo) {
      setError(t('paymentMethodNotAvailable'));
      return;
    }

    const isSupported = instances.venmo.isBrowserSupported();

    if (!isSupported) {
      setError(t('paymentMethodBrowserNotSupported'));
      return;
    }

    setError('');
    setIsInPaymentProcess(true);
    isPaymentInProgress.current = true;

    try {
      const payload = await instances.venmo.tokenize();
      const nonce = payload.nonce;

      await processPayment(nonce);
    } catch (error) {
      const braintreeError = error as { code?: string; message?: string };
      const userMessage = getVenmoTokenizeErrorMessage(braintreeError.code, t);

      if (userMessage) {
        setError(userMessage);
      }

      onPaymentError?.(userMessage);
    } finally {
      setIsInPaymentProcess(false);
      isPaymentInProgress.current = false;
    }
  }, [isInPaymentProcess, instances.venmo, processPayment, setError, onPaymentError]);

  return {
    isInitializing,
    isReady: !isInitializing && !!instances.venmo,
    handleSubmit,
  };
};
